import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { AdminTab } from "../types";

const BASE_ADMIN_TABS = [
  "Users",
  "Teams",
  "Questions",
  "Analytics",
  "Feedback",
  "Audit",
  "Integrations",
  "Zendesk",
  "Status",
  "Management",
] as const satisfies readonly AdminTab[];

const ALL_ADMIN_TABS = [...BASE_ADMIN_TABS, "Permissions", "Settings", "Sentry", "Tenants"] as const;

function isAdminTab(value: string | null): value is AdminTab {
  return ALL_ADMIN_TABS.includes(value as AdminTab);
}

export function useAdminTabs(isSuperAdmin: boolean) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = useMemo<AdminTab[]>(
    () => (isSuperAdmin ? [...BASE_ADMIN_TABS, "Permissions", "Settings", "Sentry", "Tenants"] : [...BASE_ADMIN_TABS]),
    [isSuperAdmin],
  );

  const requestedTab = searchParams.get("tab");
  const activeTab =
    isAdminTab(requestedTab) && tabs.includes(requestedTab)
      ? requestedTab
      : "Users";

  const setActiveTab = useCallback(
    (nextTab: AdminTab) => {
      const nextParams = new URLSearchParams(searchParams);
      if (nextTab === "Users") nextParams.delete("tab");
      else nextParams.set("tab", nextTab);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return {
    tabs,
    activeTab,
    setActiveTab,
  };
}
